/**
 * ConfigurationForm — форма конфигурации каналов/интеграций/модулей + живой расчёт цены.
 *
 * Прайс v3 (2026-07-01):
 * - Все мессенджеры (TG/MAX/ВК) + сайт-виджет — В БАЗЕ бесплатно (one_time=0) → показываем как «включено».
 * - Avito — единственный платный канал (тумблер, +20 000).
 * - «Умный» набор автоматизаций — включён в базу (информационный блок).
 * - Интеграции/модули — из каталога, апсейл (отдельный счёт / +мес).
 *
 * Грузит: useCatalog() + useConfiguration(dealId). Сохраняет: useSaveConfiguration(dealId).
 * Живой расчёт — клиентский, мгновенный (до сохранения).
 */

import { useEffect, useMemo, useState } from 'react'
import { Check, Loader2, Save, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useCatalog } from '@/hooks/useCatalog'
import { useConfiguration, useSaveConfiguration } from '@/hooks/useConfiguration'
import { formatRub } from '@/lib/utils'
import type { CatalogItem, PriceCatalog } from '@/types'

interface ConfigurationFormProps {
  dealId: string
  onSaved?: () => void
}

// «Умный» набор — включён бесплатно (pricing.json → smart_features).
const SMART_FEATURES = [
  'Напоминания о записи',
  'Запрос отзыва (Яндекс.Карты/2ГИС)',
  'Возврат брошенной заявки',
  'Авто-резюме диалога владельцу',
  'Квалификация лида',
  'Follow-up (3 касания)',
]

// ---- Живой расчёт на клиенте ----

interface LiveCalc {
  base: number
  extra_channels: number
  upsells_total: number
  monthly_upsells: number
  subscription_base: number
  subscription_overflow: number
  contract_total: number
  first_tranche: number
  second_tranche: number
  monthly_total: number
}

function calcLive(
  catalog: PriceCatalog,
  channels: string[],
  integrations: string[],
  modules: string[],
  dialogs_forecast: number,
): LiveCalc {
  // n(): устойчивое приведение к числу (API может отдать null/строку) → без NaN/крашей.
  const n = (v: unknown): number => {
    const x = Number(v)
    return Number.isFinite(x) ? x : 0
  }

  const base = n(catalog.base?.cost)

  // Каналы, входящие в договор. Мессенджеры/виджет = one_time 0 → добавляют 0; платит только Avito.
  const extra_channels = (catalog.channels ?? [])
    .filter((c) => channels.includes(c.id) && c.enters_contract_50_50)
    .reduce((sum, c) => sum + n(c.one_time), 0)

  // Апсейлы — интеграции
  const upsells_one_time = (catalog.integrations ?? [])
    .filter((i) => integrations.includes(i.id))
    .reduce((sum, i) => sum + n(i.one_time), 0)

  // Апсейлы — модули (one_time + monthly)
  const moduleItems = (catalog.modules ?? []).filter((m) => modules.includes(m.id))
  const modules_one_time = moduleItems.reduce((sum, m) => sum + n(m.one_time), 0)
  const monthly_upsells = moduleItems.reduce((sum, m) => sum + n(m.monthly), 0)

  const upsells_total = upsells_one_time + modules_one_time
  const contract_total = base + extra_channels
  const first_tranche = Math.round(contract_total * 0.5)
  const second_tranche = contract_total - first_tranche

  // Подписка
  const sub = catalog.subscription
  const included = n(sub?.included_dialogs)
  let subscription_overflow = 0
  const overflow_dialogs = Math.max(0, dialogs_forecast - included)
  if (overflow_dialogs > 0) {
    let remaining = overflow_dialogs
    for (const bracket of sub?.brackets ?? []) {
      if (remaining <= 0) break
      const bracket_cap = bracket.to != null ? n(bracket.to) - n(bracket.from) + 1 : Infinity
      const in_bracket = Math.min(remaining, bracket_cap)
      subscription_overflow += in_bracket * n(bracket.rate)
      remaining -= in_bracket
    }
  }

  const subscription_base = n(sub?.monthly_base)
  const monthly_total = subscription_base + subscription_overflow + monthly_upsells

  return {
    base,
    extra_channels,
    upsells_total,
    monthly_upsells,
    subscription_base,
    subscription_overflow,
    contract_total,
    first_tranche,
    second_tranche,
    monthly_total,
  }
}

// ---- Toggle checkbox row ----

function ToggleRow({
  item,
  checked,
  onChange,
  priceLabel,
  disabled,
}: {
  item: CatalogItem
  checked: boolean
  onChange: (id: string, v: boolean) => void
  priceLabel: string
  disabled?: boolean
}) {
  return (
    <label
      className={`flex items-center justify-between px-3 py-2.5 rounded-md border cursor-pointer transition-colors ${
        checked
          ? 'border-primary/40 bg-primary/5'
          : 'border-slate-200 bg-white hover:bg-slate-50'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <div className="flex items-center gap-3">
        {/* Чисто визуальный индикатор: переключение делает сам <label> через скрытый input.
            (Раньше здесь был свой onClick — двойное срабатывание с label; убрано.) */}
        <div
          className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
            checked ? 'border-primary bg-primary' : 'border-slate-300'
          }`}
        >
          {checked && <Check className="h-3 w-3 text-white" />}
        </div>
        <div>
          <span className="text-sm font-medium text-foreground">{item.name}</span>
          {item.category && (
            <span className="ml-2 text-xs text-muted-foreground">{item.category}</span>
          )}
        </div>
      </div>
      <span className="text-xs text-muted-foreground shrink-0 ml-3">{priceLabel}</span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(item.id, e.target.checked)}
      />
    </label>
  )
}

// ---- Main component ----

export function ConfigurationForm({ dealId, onSaved }: ConfigurationFormProps) {
  const { data: catalog, isLoading: catalogLoading } = useCatalog()
  const { data: configResp, isLoading: configLoading } = useConfiguration(dealId)
  const saveMutation = useSaveConfiguration(dealId)

  const [saved, setSaved] = useState(false)

  // Form state
  const [channels, setChannels] = useState<string[]>([])
  const [integrations, setIntegrations] = useState<string[]>([])
  const [modules, setModules] = useState<string[]>([])
  const [dialogsForecast, setDialogsForecast] = useState<number>(0)
  const [notes, setNotes] = useState<string>('')

  // Prefill when saved config loads
  useEffect(() => {
    const cfg = configResp?.configuration
    if (cfg) {
      setChannels(cfg.channels)
      setIntegrations(cfg.integrations)
      setModules(cfg.modules)
      setDialogsForecast(cfg.dialogs_forecast)
      setNotes(cfg.notes ?? '')
    }
  }, [configResp])

  // Каналы: в базе (one_time 0) vs платные (Avito). Мессенджеры/виджет — включены, платит только Avito.
  const baseChannels = useMemo(
    () => (catalog?.channels ?? []).filter((c) => (c.one_time ?? 0) === 0),
    [catalog],
  )
  const paidChannels = useMemo(
    () => (catalog?.channels ?? []).filter((c) => (c.one_time ?? 0) > 0),
    [catalog],
  )

  const liveCalc = useMemo(() => {
    if (!catalog) return null
    return calcLive(catalog, channels, integrations, modules, dialogsForecast)
  }, [catalog, channels, integrations, modules, dialogsForecast])

  function togglePaidChannel(id: string, val: boolean) {
    setChannels((prev) => (val ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)))
    setSaved(false)
  }
  function toggleIntegration(id: string, val: boolean) {
    setIntegrations((prev) => (val ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)))
    setSaved(false)
  }
  function toggleModule(id: string, val: boolean) {
    setModules((prev) => (val ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)))
    setSaved(false)
  }

  async function handleSave() {
    await saveMutation.mutateAsync({
      channels,
      integrations,
      modules,
      dialogs_forecast: dialogsForecast,
      notes,
    })
    setSaved(true)
    onSaved?.()
    setTimeout(() => setSaved(false), 3000)
  }

  if (catalogLoading || configLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка конфигурации...
      </div>
    )
  }

  if (!catalog) {
    return <p className="text-sm text-destructive">Не удалось загрузить каталог</p>
  }

  return (
    <div className="space-y-5">
      {/* Каналы */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Каналы</p>

        {/* В базе — бесплатно */}
        <div className="rounded-md border border-green-200 bg-green-50/60 px-3 py-2.5 mb-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Check className="h-3.5 w-3.5 text-green-600" />
            <span className="text-xs font-semibold text-green-800">Включено в базу — бесплатно</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {baseChannels.map((ch) => (
              <span
                key={ch.id}
                className="inline-flex items-center gap-1 text-xs bg-white border border-green-200 text-green-800 rounded px-2 py-0.5"
              >
                <Check className="h-3 w-3" />
                {ch.name}
              </span>
            ))}
          </div>
        </div>

        {/* Платный канал (Avito) */}
        {paidChannels.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground">Платный канал (входит в договор 50/50):</p>
            {paidChannels.map((ch) => (
              <ToggleRow
                key={ch.id}
                item={ch}
                checked={channels.includes(ch.id)}
                onChange={togglePaidChannel}
                priceLabel={`+${formatRub(ch.one_time ?? 0)}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Умный набор — включён */}
      <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-semibold text-foreground">«Умный» набор — включён бесплатно</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SMART_FEATURES.map((f) => (
            <span key={f} className="text-xs bg-white border border-primary/20 text-foreground/80 rounded px-2 py-0.5">
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Интеграции */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Интеграции <span className="normal-case font-normal text-muted-foreground">(апсейл — отдельный счёт; первая лёгкая бесплатно при внедрении)</span>
        </p>
        <div className="space-y-1.5">
          {catalog.integrations.map((it) => (
            <ToggleRow
              key={it.id}
              item={it}
              checked={integrations.includes(it.id)}
              onChange={toggleIntegration}
              priceLabel={it.one_time != null ? `${formatRub(it.one_time)} разово` : '—'}
            />
          ))}
        </div>
      </div>

      {/* Модули */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Платные модули <span className="normal-case font-normal text-muted-foreground">(апсейл)</span>
        </p>
        <div className="space-y-1.5">
          {catalog.modules.map((mod) => {
            const parts: string[] = []
            if (mod.one_time) parts.push(`${formatRub(mod.one_time)} разово`)
            if (mod.monthly) parts.push(`${formatRub(mod.monthly)}/мес`)
            return (
              <ToggleRow
                key={mod.id}
                item={mod}
                checked={modules.includes(mod.id)}
                onChange={toggleModule}
                priceLabel={parts.join(' + ') || '—'}
              />
            )
          })}
        </div>
      </div>

      {/* Прогноз диалогов */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Прогноз обращений в месяц
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={10000}
            step={10}
            className="w-28 text-sm rounded-md border px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={dialogsForecast === 0 ? '' : dialogsForecast}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10)
              setDialogsForecast(isNaN(v) ? 0 : v)
              setSaved(false)
            }}
            placeholder="напр. 150"
          />
          <span className="text-xs text-muted-foreground">
            включено {catalog.subscription.included_dialogs} разговоров/мес
          </span>
        </div>
      </div>

      {/* Заметки */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Заметки</p>
        <textarea
          className="w-full text-sm rounded-md border px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          rows={2}
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setSaved(false) }}
          placeholder="Пожелания клиента, нюансы конфигурации..."
        />
      </div>

      {/* Живой расчёт */}
      {liveCalc && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Расчёт (предпросмотр)</p>

          {/* Договор */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">База (все мессенджеры + виджет + умный набор)</span>
              <span>{formatRub(liveCalc.base)}</span>
            </div>
            {liveCalc.extra_channels > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Avito</span>
                <span>+{formatRub(liveCalc.extra_channels)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
              <span>Сумма договора (50/50)</span>
              <span>{formatRub(liveCalc.contract_total)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Предоплата 50%</span>
              <span>{formatRub(liveCalc.first_tranche)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Остаток 50%</span>
              <span>{formatRub(liveCalc.second_tranche)}</span>
            </div>
          </div>

          {/* Апсейлы */}
          {liveCalc.upsells_total > 0 && (
            <div className="border-t pt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Апсейлы (отдельный счёт)</span>
                <span>{formatRub(liveCalc.upsells_total)}</span>
              </div>
            </div>
          )}

          {/* Подписка */}
          <div className="border-t pt-2 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Подписка (база)</span>
              <span>{formatRub(liveCalc.subscription_base)}/мес</span>
            </div>
            {liveCalc.subscription_overflow > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Сверхлимит ({dialogsForecast} разговоров)</span>
                <span>+{formatRub(liveCalc.subscription_overflow)}/мес</span>
              </div>
            )}
            {liveCalc.monthly_upsells > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Модули (ежемес.)</span>
                <span>+{formatRub(liveCalc.monthly_upsells)}/мес</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold">
              <span>Итого/мес</span>
              <span>{formatRub(liveCalc.monthly_total)}/мес</span>
            </div>
          </div>
        </div>
      )}

      {/* Кнопка сохранения */}
      <div className="flex items-center gap-3">
        <Button
          size="sm"
          type="button"
          className="gap-2"
          disabled={saveMutation.isPending}
          onClick={handleSave}
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Сохранить расчёт
        </Button>
        {saved && (
          <div className="flex items-center gap-1.5 text-sm text-green-700">
            <Check className="h-4 w-4" />
            Сохранено — станет основой КП и счёта
          </div>
        )}
        {saveMutation.isError && (
          <span className="text-sm text-destructive">Ошибка при сохранении</span>
        )}
      </div>
    </div>
  )
}
